import { type ComponentType, type FC } from 'react';

import { animated, useSpring, useSpringValue } from '@react-spring/web';
import clsx from 'clsx';

import { normalizeToValue } from '@/shared/helpers/function';
import { getIsSlowDevice } from '@/shared/helpers/get-is-slow-device';

import { ICON_BY_TYPE } from '../../constants';
import { type SimplePrize } from '../../types';

import s from './PrizeCard.module.scss';

interface Props {
  className?: string;
  prize: SimplePrize;
  isMiddle?: boolean;
  isSingleMiddle?: boolean;
  priority: number;
  offsetIndex: number;
  totalLength: number;
  side: 'left' | 'right' | 'middle';
}

export const PrizeCard: FC<Props> = ({
  className,
  prize,
  isMiddle,
  isSingleMiddle,
  priority,
  offsetIndex,
  totalLength,
  side,
}) => {
  const xMultipier = side === 'right' ? -1 : 1;

  const isSlowDevice = getIsSlowDevice();

  const styles = useSpring({
    from: {
      x: `${offsetIndex * xMultipier * 100}%`,
      scale: isMiddle ? 0.85 : 1,
      rotateY: isMiddle ? '0deg' : `${xMultipier * 90}deg`,
    },
    to: {
      x: '0%',
      scale: 1,
      rotateY: '0deg',
    },
    config: {
      tension: 300,
      friction: 40,
    },
    immediate: isSlowDevice,
  });

  const offsetSpring = useSpringValue(offsetIndex, { immediate: isSlowDevice });
  const maxSize = isSingleMiddle ? 120 : 100;

  const size = offsetSpring.to({
    range: [0, 1, 2, 3],
    output: [maxSize, 80, 64, 53],
  });

  const imageX = offsetSpring.to({
    range: [0, 1, 2, 3],
    output: ['0%', `${xMultipier * 12}%`, `${xMultipier * 24}%`, `${xMultipier * 36}%`],
  });

  const icon = ICON_BY_TYPE[prize.type];

  const isReactComponent = (
    value: unknown
  ): value is ComponentType<{ className?: string }> => {
    return typeof value === 'function';
  };

  const ResolvedIcon = isReactComponent(icon) ? icon : normalizeToValue(icon, prize);

  return (
    <animated.div
      className={clsx(
        s.container,
        totalLength === 1 && s.single,
        isMiddle && s.middle,
        isSingleMiddle && s['single-middle'],
        className
      )}
      style={{
        ...styles,
        ...(totalLength === 1
          ? {}
          : { zIndex: priority, flexGrow: size, flexBasis: size, maxWidth: size }),
      }}
    >
      <animated.div className={s['card-wrapper']} style={{ x: imageX }}>
        <animated.div
          className={s.card}
          style={
            totalLength === 1
              ? undefined
              : {
                  width: size,
                  height: size,
                }
          }
        >
          <animated.div
            className={s['img-wrapper']}
            style={{
              width: size,
              height: size,
            }}
          >
            {typeof ResolvedIcon === 'function' ? (
              <ResolvedIcon className={s.image} />
            ) : (
              <img
                src={ResolvedIcon as string}
                width="100%"
                height="100%"
                className={s.image}
              />
            )}
            {/* <img
              src={normalizeToValue(icon, prize) as string}
              width="100%"
              height="100%"
              className={s.image}
            /> */}
          </animated.div>
        </animated.div>
      </animated.div>
    </animated.div>
  );
};
